import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  arrayUnion,
  query,
  where,
  getDocs,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Order status flow:
 * pending → confirmed → packaged → shipped → delivered
 *                                         ↘ cancelled
 */

/**
 * Create a new order in Firestore.
 *
 * @param {string} userId - Firebase user ID
 * @param {Object} orderData - Order details
 * @param {Array} orderData.items - Array of cart items
 * @param {number} orderData.total - Total order amount
 * @param {Object} orderData.deliveryAddress - Delivery address object
 * @returns {Promise<Object>} Created order with ID
 *
 * @example
 * const order = await createOrder(userId, {
 *   items: [{ id: 1, name: 'Rice', price: 55, quantity: 2 }],
 *   total: 110,
 *   deliveryAddress: { label: 'Home', line1: '...' }
 * });
 */
export const createOrder = async (userId, orderData) => {
  try {
    const ordersRef = collection(db, 'orders');
    const docRef = await addDoc(ordersRef, {
      userId,
      ...orderData,
      status: 'pending',
      statusHistory: [
        {
          status: 'pending',
          timestamp: new Date(),
          updatedBy: 'system',
          notes: 'Order placed successfully',
        },
      ],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { id: docRef.id, ...orderData };
  } catch (error) {
    console.error('Error creating order:', error);
    throw new Error('Failed to create order: ' + error.message);
  }
};

/**
 * Update order status - Called by delivery agent
 * Status options: 'pending', 'confirmed', 'packaged', 'shipped', 'delivered', 'cancelled'
 * @param {string} orderId - Order ID to update
 * @param {string} newStatus - New status value
 * @param {object} options - Additional options
 * @param {string} options.agentId - Delivery agent ID (for tracking who updated)
 * @param {string} options.notes - Optional notes about the status change
 * @param {object} options.location - Optional GPS location {latitude, longitude}
 */
export const updateOrderStatus = async (orderId, newStatus, options = {}) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    
    await updateDoc(orderRef, {
      status: newStatus,
      updatedAt: serverTimestamp(),
      statusHistory: arrayUnion({
        status: newStatus,
        timestamp: new Date(),
        updatedBy: options.agentId || 'system',
        notes: options.notes || '',
        location: options.location || null,
      }),
      lastStatusUpdate: {
        status: newStatus,
        timestamp: new Date(),
        agentId: options.agentId || 'system',
        notes: options.notes || '',
        location: options.location || null,
      },
    });

    return { success: true, orderId, status: newStatus };
  } catch (error) {
    console.error('Error updating order:', error);
    throw new Error('Failed to update order: ' + error.message);
  }
};

/**
 * Get all orders for a user, sorted by creation date (newest first).
 *
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Array>} Array of order objects
 */
export const getUserOrders = async (userId) => {
  try {
    const ordersRef = collection(db, 'orders');
    const q = query(ordersRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    const orders = [];
    querySnapshot.forEach((doc) => {
      orders.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    return orders.sort(
      (a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0)
    );
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw new Error('Failed to fetch orders: ' + error.message);
  }
};

/**
 * Subscribe to real-time order updates
 * Used to listen for status changes from delivery agent
 * @param {string} orderId - Order ID to listen to
 * @param {function} callback - Callback function that receives updated order data
 * @returns {function} Unsubscribe function
 */
export const subscribeToOrder = (orderId, callback) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const unsubscribe = onSnapshot(orderRef, (doc) => {
      if (doc.exists()) {
        callback({
          id: doc.id,
          ...doc.data(),
        });
      } else {
        callback(null);
      }
    });
    return unsubscribe;
  } catch (error) {
    console.error('Error subscribing to order:', error);
    throw new Error('Failed to subscribe to order: ' + error.message);
  }
};

/**
 * Update payment method for an order.
 * @param {string} orderId - Order ID
 * @param {'cash'|'upi'} paymentMethod - Selected payment method
 */
export const updateOrderPaymentMethod = async (orderId, paymentMethod) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    await updateDoc(orderRef, {
      paymentMethod,
      paymentStatus: 'pending',
      updatedAt: serverTimestamp(),
    });
    return { success: true, orderId, paymentMethod };
  } catch (error) {
    console.error('Error updating payment method:', error);
    throw new Error('Failed to update payment method: ' + error.message);
  }
};
